import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, BellIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import './NotificationsModal.css';

const NotificationsModal = ({ isOpen, onClose }) => {
  const {
    announcements,
    wishlistDeadlines,
    userNotifications,
    stats,
    notificationStats,
    loading,
    dismissAnnouncement,
    markAnnouncementAsRead,
    markUserNotificationAsRead,
    markAllAsRead,
    refreshAnnouncements,
    getTotalUnreadCount,
    deleteUserNotification,
  } = useAnnouncements();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isClosing, setIsClosing] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const announcementsListRef = useRef(null);

  // Combine all announcements and user notifications with safety checks
  const allAnnouncements = [
    ...(announcements || []),
    ...(wishlistDeadlines || [])
  ];

  // Transform user notifications to match announcement structure for display
  const transformedUserNotifications = (userNotifications || []).map(notification => ({
    ...notification,
    // Map notification fields to announcement structure
    content: notification.message,
    start_date: notification.created_at,
    category: notification.notification_type,
    category_display: notification.notification_type_display,
    priority_display: notification.priority_display,
    is_read: notification.is_read || notification.status === 'read',
    dismissible: false, // User notifications shouldn't be dismissible
    // Add a flag to identify this as a user notification
    isUserNotification: true
  }));

  // Combine all notifications
  const allNotifications = [
    ...allAnnouncements,
    ...transformedUserNotifications
  ];

  // Filter notifications by category
  const filteredNotifications = selectedCategory === 'all'
    ? allNotifications
    : selectedCategory === 'user_notifications'
    ? transformedUserNotifications
    : allNotifications.filter(notification =>
        notification && (
          notification.category === selectedCategory ||
          notification.notification_type === selectedCategory
        )
      );

  // Sort by priority and date
  const sortedNotifications = filteredNotifications.sort((a, b) => {
    const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };

    // First sort by read status (unread first)
    if (a.is_read !== b.is_read) {
      return a.is_read ? 1 : -1;
    }

    // Then by priority
    const aPriority = priorityOrder[a.priority] || 1;
    const bPriority = priorityOrder[b.priority] || 1;

    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }

    // Finally by date (newest first)
    const aDate = new Date(a.start_date || a.created_at || 0);
    const bDate = new Date(b.start_date || b.created_at || 0);
    return bDate - aDate;
  });

  // Get unique categories including user notifications
  const categories = [
    { value: 'all', label: 'All Notifications', count: allNotifications.length },
    { value: 'user_notifications', label: 'Course Updates', count: transformedUserNotifications.length },
    ...Array.from(new Set(allAnnouncements.map(ann => ann.category).filter(Boolean)))
      .map(category => ({
        value: category,
        label: category ? category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown',
        count: allAnnouncements.filter(ann => ann.category === category).length
      }))
  ];

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  // Handle keyboard navigation and focus management
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      // Tab navigation within modal
      if (e.key === 'Tab') {
        const modal = modalRef.current;
        if (!modal) return;

        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus management - focus the close button initially
      setTimeout(() => {
        if (firstFocusableRef.current) {
          firstFocusableRef.current.focus();
        }
      }, 100);

      // Announce modal opening to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `Notifications modal opened. ${stats?.total_active || 0} unread notifications.`;
      document.body.appendChild(announcement);

      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, stats?.total_active, handleClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle notification click (mark as read)
  const handleNotificationClick = async (notification) => {
    try {
      if (notification.isUserNotification) {
        if (markUserNotificationAsRead) {
          const res = await markUserNotificationAsRead(notification.id);
          if (!res?.success) console.warn('Mark user notification read failed:', res?.error);
        }
      } else {
        if (markAnnouncementAsRead) {
          const res = await markAnnouncementAsRead(notification.id);
          if (!res?.success) console.warn('Mark announcement read failed:', res?.error);
        }
      }
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  // Render notification item (shared between vertical and horizontal layouts)
  const renderNotificationItem = (notification) => (
    <div
      key={notification.id}
      className={`announcement-item ${notification.is_read ? 'read' : 'unread'} ${notification.isUserNotification ? 'user-notification' : ''}`}
      onClick={() => handleNotificationClick(notification)}
      role="button"
      tabIndex={0}
      aria-label={`${notification.is_read ? 'Read' : 'Unread'} ${notification.isUserNotification ? 'notification' : 'announcement'}: ${notification.title || 'Untitled'}. Priority: ${notification.priority_display || 'Normal'}. Category: ${notification.category_display || 'General'}. ${notification.deadline_date ? `Deadline: ${new Date(notification.deadline_date).toLocaleDateString()}` : ''}`}
      aria-describedby={`notification-content-${notification.id}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleNotificationClick(notification);
        }
      }}
    >
      {/* Read/Unread Status Indicator */}
      <div
        className="read-status-indicator"
        aria-hidden="true"
        title={notification.is_read ? 'Read' : 'Unread'}
      />

      <div className="announcement-icon">
        {notification.isUserNotification
          ? getCategoryIcon(notification.notification_type || 'registration_confirmation')
          : getCategoryIcon(notification.category || 'general_news')
        }
      </div>

      <div className="announcement-content">
        <div className="announcement-header">
          <h4 className="announcement-title" id={`notification-title-${notification.id}`}>
            {notification.title || (notification.isUserNotification ? 'Course Update' : 'Untitled Announcement')}
          </h4>
          <div className="announcement-meta">
            {getPriorityIcon(notification.priority || 'normal')}
            <span className="announcement-time" aria-label={`Posted ${formatDate(notification.start_date || notification.created_at || new Date())}`}>
              {formatDate(notification.start_date || notification.created_at || new Date())}
            </span>
          </div>
        </div>

        <p className="announcement-text" id={`notification-content-${notification.id}`}>
          {notification.content || notification.message || 'No content available'}
        </p>

        <div className="announcement-footer">
          <span className={`priority-badge ${notification.priority || 'normal'}`}>
            {notification.priority_display || 'Normal'}
          </span>
          <span className="category-badge">
            {notification.category_display || (notification.isUserNotification ? 'Course Update' : 'General')}
          </span>
          {notification.deadline_date && (
            <span className="deadline-badge">
              Deadline: {new Date(notification.deadline_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {(notification.isUserNotification) ? (
        <button
          className="dismiss-btn"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              const res = await deleteUserNotification(notification.id);
              if (!res?.success) {
                console.warn('Delete notification failed:', res?.error);
              }
            } catch (err) {
              console.error('Delete notification error:', err);
            }
          }}
          aria-label="Delete notification"
          title="Delete notification"
        >
          <XMarkIcon className="dismiss-icon" />
        </button>
      ) : (
        (notification.dismissible !== false) && (
          <button
            className="dismiss-btn"
            onClick={(e) => handleDismissAnnouncement(e, notification.id)}
            aria-label="Dismiss announcement"
          >
            <XMarkIcon className="dismiss-icon" />
          </button>
        )
      )}
    </div>
  );

  // Handle dismiss announcement
  const handleDismissAnnouncement = async (e, announcementId) => {
    e.stopPropagation();
    try {
      const res = await dismissAnnouncement(announcementId);
      if (!res?.success) console.warn('Dismiss announcement failed:', res?.error);
    } catch (err) {
      console.error('Dismiss announcement error:', err);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (markAllAsRead) {
      await markAllAsRead();
    }
  };

  // Touch handlers for mobile interactions
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > 50;
    const isDownSwipe = distance < -50;

    // Pull to refresh on down swipe at top of list
    if (isDownSwipe && announcementsListRef.current?.scrollTop === 0) {
      handlePullRefresh();
    }

    // Swipe up to close modal (mobile only)
    if (isUpSwipe && window.innerWidth <= 768) {
      const swipeDistance = Math.abs(distance);
      if (swipeDistance > 100) {
        handleClose();
      }
    }
  };

  // Pull to refresh functionality
  const handlePullRefresh = async () => {
    if (isPullRefreshing || loading) return;

    setIsPullRefreshing(true);
    try {
      await refreshAnnouncements();

      // Show success feedback
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = 'Notifications refreshed';
      document.body.appendChild(announcement);

      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    } catch (error) {
      console.error('Failed to refresh announcements:', error);
    } finally {
      setTimeout(() => {
        setIsPullRefreshing(false);
      }, 500); // Small delay for visual feedback
    }
  };

  // Get priority icon
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <ExclamationTriangleIcon className="priority-icon urgent" />;
      case 'high':
        return <ExclamationTriangleIcon className="priority-icon high" />;
      default:
        return <InformationCircleIcon className="priority-icon normal" />;
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const iconMap = {
      course_deadline: '⏰',
      system_update: '🔧',
      training_opportunity: '🎓',
      government_initiative: '🏛️',
      general_news: '📢',
      maintenance: '⚙️',
      enrollment: '📝'
    };
    return iconMap[category] || '📢';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`notifications-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="notifications-modal-title"
    >
      <div 
        ref={modalRef}
        className={`notifications-modal ${isClosing ? 'closing' : ''}`}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <BellIcon className="modal-title-icon" />
            <h2 id="notifications-modal-title">
              Notifications
              {getTotalUnreadCount && getTotalUnreadCount() > 0 && (
                <span className="unread-count">{getTotalUnreadCount()}</span>
              )}
            </h2>
          </div>
          
          <div className="modal-header-actions">
            {(stats?.total_active || 0) > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                disabled={loading}
              >
                Mark all as read
              </button>
            )}
            
            <button
              ref={firstFocusableRef}
              className="modal-close-btn"
              onClick={handleClose}
              aria-label="Close notifications"
            >
              <XMarkIcon className="close-icon" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(category => (
            <button
              key={category.value}
              className={`category-btn ${selectedCategory === category.value ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.value)}
            >
              {category.label}
              {category.count > 0 && (
                <span className="category-count">{category.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Announcements List (Vertical scrolling for all devices) */}
        <div
          className="announcements-list"
          ref={announcementsListRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isPullRefreshing && (
            <div className="pull-refresh-indicator">
              <div className="loading-spinner"></div>
              <p>Refreshing notifications...</p>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : sortedNotifications.length === 0 ? (
            <div className="empty-state">
              <BellIcon className="empty-icon" />
              <h3>No notifications</h3>
              <p>You're all caught up! Check back later for new announcements.</p>
            </div>
          ) : (
            sortedNotifications.map(renderNotificationItem)
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;
